import { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Provider } from 'react-redux'
import { store } from './store/index'
import { useAppSelector, useAppDispatch, raceActions } from './store/index'
import type { UserRole } from './types/index'

const isOrganizerRole = (role: UserRole | null) => role === 'system_admin' || role === 'race_organizer'
import { LoginPage } from './pages/LoginPage'
import { MapPage } from './pages/MapPage'
import { LogPage } from './pages/LogPage'
import { TeamPage } from './pages/TeamPage'
import { AdminPage } from './pages/AdminPage'
import { ProfilePage } from './pages/ProfilePage'
import { ConsentPage } from './pages/ConsentPage'
import { SmsExamplePage } from './pages/SmsExamplePage'
import { OrgDashboardPage } from './pages/OrgDashboardPage'
import { OrgRaceLogPage } from './pages/OrgRaceLogPage'
import { OrgMapPage } from './pages/OrgMapPage'
import { OrgRacePage } from './pages/OrgRacePage'
import { OrgLegEditorPage } from './pages/OrgLegEditorPage'
import { SpectatorPage } from './pages/SpectatorPage'
import { SystemAdminPage } from './pages/SystemAdminPage'
import { RegisterPage } from './pages/RegisterPage'
import { MobileOnlyPage } from './pages/MobileOnlyPage'

function ProtectedRoute({
  children,
  requireAdmin = false,
  requireOrganizer = false,
  spectatorBlocked = false,
}: {
  children: React.ReactNode
  requireAdmin?: boolean
  requireOrganizer?: boolean
  spectatorBlocked?: boolean
}) {
  const isAuthenticated = useAppSelector(state => state.ui.isAuthenticated)
  const userRole = useAppSelector(state => state.ui.userRole)

  if (!isAuthenticated) return <Navigate to="/login" replace />

  // team_admin has no web access — send them to the mobile-only page
  if (userRole === 'team_admin') return <Navigate to="/mobile-only" replace />

  if (requireOrganizer && !isOrganizerRole(userRole)) return <Navigate to="/map" replace />

  if (requireAdmin && userRole !== 'system_admin' && userRole !== 'race_organizer') {
    return <Navigate to="/map" replace />
  }

  if (spectatorBlocked && userRole === 'spectator') return <Navigate to="/map" replace />

  return <>{children}</>
}

function AppContent() {
  const dispatch = useAppDispatch()
  const timerIsRunning = useAppSelector(state => state.race.timer.isRunning)

  useEffect(() => {
    if (timerIsRunning) {
      const id = setInterval(() => dispatch(raceActions.tickTimer()), 1000)
      return () => clearInterval(id)
    }
  }, [timerIsRunning, dispatch])

  return (
    <Router>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/consent" element={<ConsentPage />} />
        <Route path="/sms-example" element={<SmsExamplePage />} />
        <Route path="/watch/:slug/:bib" element={<SpectatorPage />} />

        {/* Mobile-only redirect for team_admin */}
        <Route path="/mobile-only" element={<MobileOnlyPage />} />

        {/* Team member routes (runner, spectator) */}
        <Route path="/map" element={<ProtectedRoute><MapPage /></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
        <Route path="/log" element={<ProtectedRoute spectatorBlocked><LogPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute spectatorBlocked><ProfilePage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminPage /></ProtectedRoute>} />

        {/* Race organizer routes */}
        <Route path="/org-map" element={<ProtectedRoute requireOrganizer><OrgMapPage /></ProtectedRoute>} />
        <Route path="/org-log" element={<ProtectedRoute requireOrganizer><OrgRaceLogPage /></ProtectedRoute>} />
        <Route path="/org" element={<ProtectedRoute requireOrganizer><OrgDashboardPage /></ProtectedRoute>} />
        <Route path="/org/races/:raceId" element={<ProtectedRoute requireOrganizer><OrgRacePage /></ProtectedRoute>} />
        <Route path="/org/races/:raceId/legs/:legId" element={<ProtectedRoute requireOrganizer><OrgLegEditorPage /></ProtectedRoute>} />

        {/* System admin routes */}
        <Route path="/sysadmin" element={<ProtectedRoute requireAdmin><SystemAdminPage /></ProtectedRoute>} />

        <Route path="/" element={<Navigate to="/map" replace />} />
        <Route path="*" element={<Navigate to="/map" replace />} />
      </Routes>
    </Router>
  )
}

export default function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  )
}
